import { MasterInfoState } from '../definitions/MasterInfoState';
import { DISPLAY_ITEM_ID } from '../definitions/DisplayItemId';
import { MasterDetailList, PcmCodeDivision, PcmCodeInfo } from '../../../../external/MasterDetailApi';
import { PcmCdDivision } from '../definitions/PcmCdDivision';
import { MasterAnnotationProps } from '../../../../external/MasterAnnotationsApi';

export const getPcmCdNmFromMasterInfo = (masterInfo: MasterInfoState, displayItemId: DISPLAY_ITEM_ID, pcmCd: string): string => {
  // 画面項目IDを元にPCMコード区分取得
  const pcmCdDivision = masterInfo.displayItemList.find(
    (displayItem) => displayItem.displayItemId === displayItemId
  )?.setValuePcmCdDivision;
  // PCMコード区分及びPCMコードを元にPCMコード名称取得
  const pcmCdNm = masterInfo.pcmCodeDivisionList
    .find((pcm) => pcm.pcmCdDivision === pcmCdDivision)
    ?.pcmCodeList.find((pcm) => pcm.pcmCd === pcmCd)?.pcmCdNm;
  return pcmCdNm ? pcmCdNm : '';
};

export const getMessageFromMasterInfo = (masterInfo: MasterInfoState, messageId: string, ...args: string[]): string => {
  let message = masterInfo?.messageList?.find((messageItem) => messageItem.messageId === messageId)?.message ?? '';
  args.forEach((arg, index) => {
    message = message.replace('${' + index + '}', arg);
  });
  return message;
};

/**
 * PCM項目区分(pcmCdDivision)に該当するPCMコードリストを返す
 * @param masterInfo
 * @param pcmCdDivision
 * @returns
 */
export const getPcmCodeListFromMasterInfo = (masterInfo: MasterInfoState, pcmCdDivision: PcmCdDivision): PcmCodeInfo[] => {
  return (
    masterInfo.pcmCodeDivisionList.find((item) => {
      return item.pcmCdDivision === pcmCdDivision;
    })?.pcmCodeList || []
  );
};

/**
 * pcmCdDivision,pcmCd指定でPcmCdNmを取得します
 * @param masterInfo
 * @param pcmCdDivision
 * @param pcmCd
 * @returns pcmNm or blank
 */
export const getPcmCdNmByPcmCdDivisionFromMasterInfo = (masterInfo: MasterInfoState, pcmCdDivision: string, pcmCd: string): string => {
  // 検索キーであるパラメータが指定されない場合は、ブランクを返す
  if (!pcmCdDivision || !pcmCd) {
    return '';
  }
  // masterInfoが不正である場合、ブランクを返す
  if (!masterInfo?.pcmCodeDivisionList) {
    return '';
  }
  // pcmCdDivisionで一意のマスタであるため、findで検索します
  const pcmCodeList = masterInfo.pcmCodeDivisionList.find((item) => {
    return item.pcmCdDivision === pcmCdDivision;
  })?.pcmCodeList;

  // 検索結果が0件であった場合は、ブランクを返す
  if (!pcmCodeList || pcmCodeList.length === 0) {
    return '';
  }

  return (
    pcmCodeList.find((item) => {
      return item.pcmCd === pcmCd;
    })?.pcmCdNm ?? ''
  );
};

/**
 * 機種コードから、機種名を取得します
 * @param masterInfo
 * @param pcmCd
 * @returns
 */
export const getModelLongNameFromMasterInfo = (masterInfo: MasterInfoState, pcmCd: string): string => {
  return masterInfo?.itemImageList?.find((item) => item.itemCode === pcmCd)?.modelLongName || '';
};

/**
 * 並び替えのPCMコードリストを返す
 * @param masterInfo
 * @param displayItemId
 * @param pcmCdList
 * @returns
 */
export const getPcmCdDisplayOrderByPcmCdDivisionFromMasterInfo = (
  masterInfo: MasterInfoState,
  displayItemId: DISPLAY_ITEM_ID,
  pcmCdList: string[]
): string[] => {
  // 画面項目IDを元にPCMコード区分取得
  const pcmCdDivision = masterInfo?.displayItemList?.find(
    (displayItem) => displayItem.displayItemId === displayItemId
  )?.setValuePcmCdDivision;

  // PCMコード区分ごとのPCMコード表示順によりPCMコードの並び替え
  const pcmCdDisplayList: string[] = [];
  masterInfo?.pcmCodeDivisionList
    ?.find((pcm: PcmCodeDivision) => pcm.pcmCdDivision === pcmCdDivision)
    ?.pcmCodeList.map((pcm: PcmCodeInfo) => {
      if (pcmCdList.includes(pcm.pcmCd.toString())) {
        const pcmCd = pcmCdList.find((item) => item === pcm.pcmCd.toString());
        pcmCd && pcmCdDisplayList.push(pcmCd);
      }
    });

  return pcmCdDisplayList;
};

/**
 * displayItemId指定でpcmCdDivisionを取得し該当するPCMコードリストを返す
 * @param masterInfo
 * @param displayItemId
 * @returns
 */
export const getPcmCdAndNameListByDisplayItemId = (masterInfo: MasterInfoState, displayItemId: DISPLAY_ITEM_ID): MasterDetailList[] => {
  // 画面項目IDを元にPCMコード区分取得
  const pcmCdDivision = masterInfo.displayItemList.find(
    (displayItem) => displayItem.displayItemId === displayItemId
  )?.setValuePcmCdDivision;

  // PCMコード区分が取得できなかった場合は、空配列を返却して終了
  if (!pcmCdDivision) {
    return [];
  }
  // 取得したsetValuePcmCdDivisionの値にて、pcmCodeListを取得する
  // pcmCdDisplayOrderの昇順で並び替える
  const pcmCodeList = getPcmCodeListFromMasterInfo(masterInfo, pcmCdDivision as PcmCdDivision);
  return [...pcmCodeList]
    .sort((a, b) => a.pcmCdDisplayOrder - b.pcmCdDisplayOrder)
    .map((el) => {
      return {
        pcmCd: el.pcmCd,
        pcmCdNm: el.pcmCdNm,
      };
    });
};

/**
 * 注釈IDに該当する注釈情報を返す
 * @param masterInfo
 * @param annotationId
 * @returns
 */
export const getAnnotationMasterListFromMasterInfo = (masterInfo: MasterInfoState, annotationId: number): MasterAnnotationProps => {
  const annotationList = masterInfo.annotationMstList.find((item) => item.annotationId === annotationId);
  return annotationList
    ? {
        ...annotationList,
        linkUrl: annotationList.linkUrl && `${annotationList.linkUrl}`,
      }
    : ({} as MasterAnnotationProps);
};
